import { useMemo } from 'react'
import useTranslation from 'next-translate/useTranslation'

import Calendar from '../../../../calendar/components/Calendar'
import BodyText from '../../../../app/components/BodyText'
import useAppDispatch from '../../../../../store/hooks/useAppDispatch'
import useAppSelector from '../../../../../store/hooks/useAppSelector'
import { clientContextCurrentUserCompanyNameSelector } from '../../../../clientContext/store/clientContextSelectors'
import { outletsSelectedOutletListItemSelector } from '../../../../outlets/store/outletsSelectors'
import getOutletToday from '../../../../outlet/utils/getOutletToday'
import {
  preOrderNewOrderDateSelector,
  preOrderSettingsSelector,
} from '../../../store/preOrderSelectors'
import { setNewOrderDetails } from '../../../store/preOrderSlice'
import getCompanyOrderMaxDate from '../../../utils/getCompanyOrderMaxDate'

import styles from './DateStep.module.scss'

/** The company calendar: from today until the end of the company horizon */
export default function DateStep() {
  const { t } = useTranslation('preOrder')
  const dispatch = useAppDispatch()

  const orderDate = useAppSelector(preOrderNewOrderDateSelector)
  const preOrderSettings = useAppSelector(preOrderSettingsSelector)
  const outlet = useAppSelector(outletsSelectedOutletListItemSelector)
  const companyName = useAppSelector(
    clientContextCurrentUserCompanyNameSelector,
  )

  // Reading today in render is safe: the panel only mounts after a click,
  // never on the server. Days are the restaurant's, once there is one.
  const { minDate, maxDate } = useMemo(() => {
    const today = getOutletToday(outlet)

    return {
      minDate: today,
      maxDate: getCompanyOrderMaxDate(preOrderSettings, today),
    }
  }, [outlet, preOrderSettings])

  function handleSelectDate(date: string) {
    if (date !== orderDate) {
      // Order times differ per day
      dispatch(setNewOrderDetails({ orderDate: date, orderTime: null }))
    }
  }

  return (
    <div className={styles.container}>
      <Calendar
        minDate={minDate}
        maxDate={maxDate}
        initialSelectedDate={orderDate ?? undefined}
        onSelectDate={handleSelectDate}
      />
      <BodyText color="body-1" fontSize="1.2rem">
        {t('newOrderPanel.horizon', {
          companyName,
          date: maxDate.format('ll'),
        })}
      </BodyText>
    </div>
  )
}
